import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { createCollection } from '@common/utils/create-collection.util';
import {
    createBoxCustomisationSelectionDto,
    IBoxCustomisationSelectionDto,
} from '@src/api/model/box-customisation-selection.dto';
import {
    IPutBoxCustomisationSelectionRequestBody,
    packHouseApiRequest,
    TPackhouseV1SubscriptionIdBoxCustomisationSelectionGetError,
    TPackhouseV1SubscriptionIdBoxCustomisationSelectionPutError,
} from '@src/api/pack-house-generated-api';
import { queryClient } from '@src/shared/query-client';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';

const NAME = 'box-customisation-selection';

const useGetBoxCustomisationSelectionBySubscriptionId = (
    subscriptionId: string,
    queryParameters: { box_customisation_config_id?: string },
    queryOptions: { enabled: boolean }
): UseQueryResult<
    ICollection<IBoxCustomisationSelectionDto>,
    IBEError<TPackhouseV1SubscriptionIdBoxCustomisationSelectionGetError>
> => {
    return useQuery<
        ICollection<IBoxCustomisationSelectionDto>,
        IBEError<TPackhouseV1SubscriptionIdBoxCustomisationSelectionGetError>
    >(
        `${NAME}-${subscriptionId}`,
        () => {
            return packHouseApiRequest
                .packhouseV1SubscriptionIdBoxCustomisationSelectionGet(subscriptionId, queryParameters, {})
                .then((r) =>
                    createCollection(
                        r?.data?.list?.map((item) => createBoxCustomisationSelectionDto(item)),
                        0,
                        {},
                    ),
                );
        },
        queryOptions,
    );
};

const useUpdateBoxCustomisationSelection = (): UseMutationResult<
    ICollection<IBoxCustomisationSelectionDto>,
    IBEError<TPackhouseV1SubscriptionIdBoxCustomisationSelectionPutError>,
    {
        subscriptionId: string;
        body: IPutBoxCustomisationSelectionRequestBody;
    }
> => {
    return useMutation(
        (props) => {
            return packHouseApiRequest
                .packhouseV1SubscriptionIdBoxCustomisationSelectionPut(props.subscriptionId, props.body, {})
                .then((r) =>
                    createCollection(
                        r?.data?.list?.map((item) => createBoxCustomisationSelectionDto(item)),
                        0,
                        {},
                    ),
                );
        },
        {
            mutationKey: `${NAME}-update`,
            onSuccess: async (response, props) => {
                queryClient.setQueryData(`${NAME}-${props.subscriptionId}`, response);
            },
        },
    );
};

const getBoxCustomisationSelectionFromCache = (
    subscriptionId: string,
): ICollection<IBoxCustomisationSelectionDto> | undefined => {
    return queryClient.getQueryData(`${NAME}-${subscriptionId}`);
};

export const boxCustomisationSelection = {
    useGetBoxCustomisationSelectionBySubscriptionId,
    getBoxCustomisationSelectionFromCache,
    useUpdateBoxCustomisationSelection,
};
